/**
 * Path mechanics flat region per scope §6.3. Atlas Day 2 Phase 4.
 *
 * Surface (per Sol D8 lock 2026-05-27): "Path mechanics — ISG operates N
 * fulfillment approaches; M inactive. Type distribution: K merchant-managed,
 * J fulfillment-service-managed, L dropship. Engine branches by path type
 * and metadata flags."
 *
 * D8 phrasing: explicit "1 dropship path inactive" framing per scope §4.3
 * IS-vs-DOING filter — honest to substrate state (6 active + 1 inactive).
 *
 * Click handler stub for Phase 4 (Phase 6 wires inspect navigation per B.3).
 */

import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { GitBranch } from 'lucide-react';
import type { PathMechanicsSubstrate } from '../../../lib/atlas/substrateCalcSubstrate';

export interface PathMechanicsRegionProps {
  substrate: PathMechanicsSubstrate;
}

// Per scope §6.3 + Sol D8 lock 2026-05-27: render inactive count when > 0
// per IS-vs-DOING filter; otherwise omit inactive clause.
function buildSurface(substrate: PathMechanicsSubstrate): string {
  const { total, active, byType } = substrate;
  const inactive = total - active;
  const merchant = byType.merchant_managed?.active ?? 0;
  const fulfilled = byType.fulfillment_service_managed?.active ?? 0;
  const dropshipActive = byType.dropship?.active ?? 0;
  const dropshipInactive = (byType.dropship?.total ?? 0) - dropshipActive;
  // D8 shape: "ISG operates N fulfillment approaches; X dropship path inactive"
  const leadingClause = `ISG operates ${active} fulfillment ${active === 1 ? 'approach' : 'approaches'}`;
  const inactiveClause =
    inactive > 0
      ? `; ${inactive} ${dropshipInactive === inactive ? 'dropship path' : 'paths'} inactive`
      : '';
  const distClause = `Type distribution: ${merchant} merchant-managed, ${fulfilled} fulfillment-service-managed${dropshipActive > 0 ? `, ${dropshipActive} dropship` : ''}.`;
  return `${leadingClause}${inactiveClause}. ${distClause} Engine branches by path type and metadata flags.`;
}

export default function PathMechanicsRegion({ substrate }: PathMechanicsRegionProps): JSX.Element {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      data-sub-region-id="path-mechanics"
      onClick={() => navigate('/admin/engine-atlas/inspect/path-mechanics')}
      className="w-full text-left border border-border-subtle bg-surface-0 rounded-md p-3 hover:border-accent transition-colors cursor-pointer"
    >
      <div className="flex items-start gap-2">
        <GitBranch size={16} className="text-ink-secondary flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-ink-bright">Path mechanics</h3>
          <p className="text-xs text-ink-secondary mt-1">{buildSurface(substrate)}</p>
        </div>
      </div>
    </button>
  );
}
